import { ReactNode } from 'react';
import {
  Box,
  Container,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Stack,
  Text,
} from '@chakra-ui/react';
import { sizeVars } from '@duke-hq/ui/constants/layout';
import { pageContentPadding } from '@duke-hq/ui/theme/tokens';
import { EditorToolbar, EditorAction } from './EditorToolbar';

type EditorToolbarConfig = {
  breadcrumbs?: ReactNode;
  undoRedo?: { canUndo: boolean; canRedo: boolean; onUndo: () => void; onRedo: () => void };
  staleEditor?: { isStale: boolean; entityLabel: string; onLoadLatest: () => void };
  status?: ReactNode;
  actions?: EditorAction[];
};

/**
 * The body region beneath the toolbar comes in three shapes:
 * - `content`: a padded, scrolling column (course, unit hub, unit guide). The
 *   only knob is `maxW` — `'3xl'` gives the unit guide a centred reading column;
 *   everything else fills the width. The shared `pageContentPadding` and
 *   `Stack spacing` give every content editor the same rhythm as the rest of the
 *   admin app (see ContentGenerationLayout).
 * - `canvas`: a full-bleed positioning context with no padding or scroll; the
 *   child owns its own pan/zoom and any absolutely-positioned panels
 *   (conversation editor).
 * - `panes`: full-bleed and clipped; the surface renders its own side-by-side
 *   panes and controls their widths and per-pane scroll (assessment editor).
 */
type EditorBody =
  | { kind: 'content'; maxW?: 'full' | '3xl'; children: ReactNode }
  | { kind: 'canvas'; children: ReactNode }
  | { kind: 'panes'; children: ReactNode };

type EditorDrawer = {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  body: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnOverlayClick?: boolean;
};

/**
 * The shared frame for every admin content editor: a viewport-bounded column
 * with the pinned `EditorToolbar` on top and the editor body below. It owns the
 * height/scroll model and the consistent padded container, so the five editors
 * (conversation, unit-guide, course, unit hub, assessment) read as one product
 * rather than five hand-tuned layouts. The body's interior (canvas, two-pane,
 * reading column, cards) stays per-surface; the layout only frames it.
 *
 * Optional `drawer` renders a right-hand settings drawer (the same scaffold the
 * content-generation pages use). `children` are siblings of the shell, for
 * modals and confirmation dialogs.
 */
export function EditorLayout({
  toolbar,
  body,
  heightMode = 'navbar-offset',
  drawer,
  children,
}: {
  toolbar: EditorToolbarConfig;
  body: EditorBody;
  /** 'navbar-offset' (default) bounds to `calc(100vh - navbar)`; 'fill' uses
   *  100% for surfaces already bounded by a parent (the conversation canvas). */
  heightMode?: 'navbar-offset' | 'fill';
  drawer?: EditorDrawer;
  children?: ReactNode;
}) {
  const height = heightMode === 'fill' ? '100%' : `calc(100vh - ${sizeVars.navbar})`;

  return (
    <>
      <Flex flexDir="column" h={height} w="100%">
        <EditorToolbar {...toolbar} />
        {body.kind === 'content' ? (
          // Column scroll container so the inner Container's bottom padding is
          // part of the scroll flow and isn't clipped at scroll-end (a flex +
          // overflow quirk that otherwise eats the bottom gap on taller pages).
          <Flex flexDir="column" flex="1" minH={0} overflowY="auto">
            <Container maxW="full" py={pageContentPadding.y} px={pageContentPadding.x}>
              <Box
                w="100%"
                maxW={body.maxW ?? 'full'}
                mx={body.maxW === '3xl' ? 'auto' : undefined}
              >
                <Stack spacing="6">{body.children}</Stack>
              </Box>
            </Container>
          </Flex>
        ) : (
          <Flex
            flex="1"
            minH={0}
            w="100%"
            position="relative"
            overflow={body.kind === 'panes' ? 'hidden' : undefined}
          >
            {body.children}
          </Flex>
        )}
      </Flex>

      {drawer ? (
        <Drawer
          isOpen={drawer.isOpen}
          placement="right"
          onClose={drawer.onClose}
          size={drawer.size ?? 'md'}
          closeOnOverlayClick={drawer.closeOnOverlayClick}
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>
              <Text as="h2" textStyle="2xl" fontWeight="medium">
                {drawer.title}
              </Text>
            </DrawerHeader>
            <DrawerBody>{drawer.body}</DrawerBody>
            {drawer.footer != null ? <DrawerFooter>{drawer.footer}</DrawerFooter> : null}
          </DrawerContent>
        </Drawer>
      ) : null}

      {children}
    </>
  );
}
